import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { filter, map, Observable, Subject } from 'rxjs';
import { CreditReport } from '@app/app/interfaces/credit-reports/credit-report.model';
import { FinancesState } from '@app/app/store/finances/finances.state';
import {
  GetCommercialCreditReport,
  GetOwnerCreditReport,
  GetPersonalCreditReport
} from '@app/app/store/finances/finances.actions';
import { Business } from '@app/app/interfaces/business.model';
import { BusinessState } from '@app/app/store/businesses/business.state';
import { take, takeUntil } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@app/app/components/dialogs/confirm-dialog/confirm-dialog.component';
import { BorrowerValue } from '@app/app/interfaces/borrower-value.model';
import { Contact } from '@app/app/interfaces/contact.model';
import { ContactsState } from '@app/app/store/contacts/contacts.state';

interface OutstandingLoan {
  position: number;
  lenderName: string;
  balance: number;
  updatedDate: string;
  paidOff: boolean;
}

@Component({
  selector: 'app-finances-page',
  templateUrl: 'finances-page.component.html',
  styleUrls: ['finances-page.component.scss'],
})
export class FinancesPageComponent implements OnInit, OnDestroy {

  performanceAliases = [
    'annualRevenue', // 72
    // 'MONTHLY REVENUE - PROBABLY CALCULATED',
    'averageMonthlyPayroll', // 862
    'annual_profits', // 4
    'average_monthly_sales', // 466
    'leasemortgage_payment', // 242
    // 'OUTSTANDING INVOICES',
    'acceptsCredit', // 723
    'monthlyCharges', // 76
    'businessDebtAmount', // 766
    'monthlyDebtPayments', // 769
  ];

  creditDetailsAliases = [
    'publicRecordsCount', // 753
    'installmentBalance', // 754
    'revolvingBalance', // 755
    'realEstateBalance', // 756
    'revolvingAvailablePercent', // 757
    'delinquenciesOver30Days', // 760
    'delinquenciesOver60Days', // 761
    'delinquenciesOver90Days', // 762
    'derogCounter',  // 763
    'inquiriesDuringLast6Months', // 758
    'monthlyPaymentOnInstallmentTradelines', // 1886
    'totalTradeItems', // 759
  ];

  // commercialCredit$: Observable<CreditReport> = this.store.select( FinancesState.commercialCredit );
  personalCredit$: Observable<CreditReport>;
  business$: Observable<Business | null> = this.store.select( BusinessState.business );
  outstandingLoans$: Observable<OutstandingLoan[]> = this.store.select( FinancesState.outstandingLoans );
  outstandingLoansDataSource$: Observable<MatTableDataSource<any>>;
  performanceValues$: Observable<BorrowerValue[]> = this.store.select( BusinessState.borrowerValuesByAliases(this.performanceAliases) );
  creditDetailsValues$: Observable<BorrowerValue[]> = this.store.select( BusinessState.borrowerValuesByAliases(this.creditDetailsAliases) );
  ownerContacts$: Observable<Contact[]> = this.store.select( ContactsState.ownerContacts );
  primaryContact$: Observable<Contact | undefined> = this.store.select( ContactsState.primaryContact );
  loading$: Observable<boolean> = this.store.select( FinancesState.loading );
  destroyed$ = new Subject<boolean>();

  outstandingLoansTableColumns = [
    'position',
    'lenderName',
    'balance',
    'updatedDate',
    'paidOff',
  ];

  constructor(
    private dialog: MatDialog,
    private store: Store,
  ) {}

  ngOnInit():void  {
    this.primaryContact$.pipe(
      takeUntil(this.destroyed$),
      filter( pc => !!pc && !!pc.id),
    ).subscribe( primaryContact => {
      this.personalCredit$ = this.store.select( FinancesState.creditReportByContactId(primaryContact?.id as number) );
    });
    this.outstandingLoansDataSource$ = this.outstandingLoans$.pipe(
      map(loans => {
        return new MatTableDataSource(loans);
      }),
    );
  }

  pullCommercialCredit() {
    const dialogRef = this.dialog.open( ConfirmDialogComponent, {
      data: {
        title: 'Are you sure you want to pull credit?',
        description: 'Hard credit inquiries usually show on credit reports and impact credit scores.',
        confirmLabel: 'Pull credit'
      }
    });

    dialogRef.componentInstance.onCancel.pipe(
      take(1),
    ).subscribe( () => dialogRef.close() );
    dialogRef.componentInstance.onConfirm.pipe(
      take(1),
    ).subscribe( () => {
      dialogRef.close();
      this.primaryContact$.pipe(
        take(1),
      ).subscribe( primaryContact => {
        // temporarily using personal credit to mock commmercial credit flow
        !!primaryContact?.id ? this.store.dispatch( new GetOwnerCreditReport(primaryContact.id) ) : false;
      });
    });
  }

  pullPersonalCredit() {
    this.business$.pipe(
      take(1),
    ).subscribe( business => {
      !!business?.id ? this.store.dispatch( new GetPersonalCreditReport(business.id) ) : false;
    });
  }

  pullOwnerCredit(ownerContactId: number) {

  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
